import type { CallOutcome, DataQuality } from '@marnic/shared';
import { DISPOSITIONS_BY_CODE } from '@marnic/shared/dispositions';

const OUTCOME_STYLE: Record<CallOutcome, string> = {
  advanced: 'bg-emerald-100 text-emerald-800',
  committed: 'bg-emerald-100 text-emerald-800',
  won: 'bg-emerald-100 text-emerald-800',
  held_steady: 'bg-sky-100 text-sky-800',
  stalled: 'bg-amber-100 text-amber-800',
  regressed: 'bg-red-100 text-red-800',
  lost: 'bg-red-100 text-red-800',
  unknown: 'bg-ink-100 text-ink-700',
};

const DATA_QUALITY_STYLE: Record<DataQuality, string> = {
  complete: 'bg-emerald-100 text-emerald-800',
  partial: 'bg-amber-100 text-amber-800',
  fragmentary: 'bg-red-100 text-red-800',
};

interface Props {
  callOutcome: CallOutcome | undefined;
  dataQuality: DataQuality | undefined;
  callType: string | undefined;
  suggestedDisposition: string | null;
  onApplyDisposition?: (code: string) => void;
}

export function OutcomeStrip({ callOutcome, dataQuality, callType, suggestedDisposition, onApplyDisposition }: Props): JSX.Element | null {
  const hasContent = callOutcome || dataQuality || callType || suggestedDisposition;
  if (!hasContent) return null;
  const disposition = suggestedDisposition ? DISPOSITIONS_BY_CODE[suggestedDisposition] : undefined;

  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
      {callOutcome && (
        <span className={`rounded px-2 py-0.5 font-medium capitalize ${OUTCOME_STYLE[callOutcome]}`}>
          {callOutcome.replace(/_/g, ' ')}
        </span>
      )}
      {callType && (
        <span className="rounded bg-ink-100 px-2 py-0.5 text-ink-700 capitalize">
          {callType.replace(/_/g, ' ')}
        </span>
      )}
      {dataQuality && (
        <span className={`rounded px-2 py-0.5 ${DATA_QUALITY_STYLE[dataQuality]}`} title="LLM's confidence in the call data it analysed">
          data: {dataQuality}
        </span>
      )}
      {disposition && onApplyDisposition && (
        <button
          type="button"
          className="ml-auto btn-secondary !text-[11px] !px-2 !py-0.5"
          onClick={() => onApplyDisposition(disposition.code)}
          title="AI suggests this disposition based on the conversation"
        >
          Apply: {disposition.label}
        </button>
      )}
    </div>
  );
}
